import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    QuickTagDatasetPage,
    QuickTagConfigPage,
    QuickTagCodeframePage
} from './wizardPages';
import {
    FileBasedQuickTagDetails,
    QUICKTAG_TYPES,
    QuickTagImportFileType,
    QuickTagFileColumnDescriptor
} from '@nexxt/common/types';
import {
    SplitPaneModal,
    SplitPaneModalActions,
    SplitPaneModalContent
} from 'inca-ui';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import config from '../../../config';
import Loader from '../../common/Loader/Loader';
import http from '../../../utils/http';
import { QuickTagCredits } from '../types';
import { sum } from 'lodash';
import { UilChannel } from '@iconscout/react-unicons';

enum QuickTagCreationStep {
    UPLOAD_DATASET = 'Upload',
    CONFIGURE_DATASET = 'Configure',
    GENERATE_CODEFRAME = 'Code'
}

const QUICKTAG_MAX_ROWS = 3000;

interface QuickTagCreationWizardProps {
    showQuickTagCreationWizard: boolean;
    setShowQuickTagCreationWizard: (show: boolean) => void;
    quickTagCredits: QuickTagCredits;
}

export interface QuickTagCreationWizardPageProps {
    datasetFileType: QuickTagImportFileType;
    setDatasetFileType: (fileType: QuickTagImportFileType) => void;
    datasetFileUrl: string;
    setDatasetFileUrl: (datasetFileUrl: string) => void;
    datasetFileDescriptors: QuickTagFileColumnDescriptor[];
    setDatasetFileDescriptors: (
        descriptors: QuickTagFileColumnDescriptor[]
    ) => void;
    datasetHasDuplicateVariables: boolean;
    setDatasetHasDuplicateVariables: (bool: boolean) => void;
    selectedVerbatimDescriptors: QuickTagFileColumnDescriptor[];
    setSelectedVerbatimDescriptors: (
        descriptor: QuickTagFileColumnDescriptor[]
    ) => void;
    selectedLangDescriptor: QuickTagFileColumnDescriptor;
    setSelectedLangDescriptor: (
        descriptor: QuickTagFileColumnDescriptor
    ) => void;
    selectedIdDescriptor: QuickTagFileColumnDescriptor;
    setSelectedIdDescriptor: (descriptor: QuickTagFileColumnDescriptor) => void;
    datasetLabel: string;
    setDatasetLabel: (label: string) => void;
    useAiGeneratedCodeFrame: boolean;
    setUseAiGeneratedCodeFrame: (use: boolean) => void;
    codeFrameLabels: string[];
    setSelectedExistingCodeFrameLabel: (label: string) => void;
    selectedExistingCodeFrameLabel: string;
    setSelectedSheetName: (sheetName: string) => void;
    selectedSheetName: string;
}

function QuickTagCreationWizard(props: QuickTagCreationWizardProps) {
    const [activeStepIdx, setActiveStepIdx] = useState(0);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedVerbatimDescriptors, setSelectedVerbatimDescriptors] =
        useState<QuickTagFileColumnDescriptor[]>([]);
    const [selectedLangDescriptor, setSelectedLangDescriptor] =
        useState<QuickTagFileColumnDescriptor>();
    const [selectedIdDescriptor, setSelectedIdDescriptor] =
        useState<QuickTagFileColumnDescriptor>();
    const [datasetFileType, setDatasetFileType] =
        useState<QuickTagImportFileType>();
    const [datasetFileUrl, setDatasetFileUrl] = useState<string>();
    const [datasetFileDescriptors, setDatasetFileDescriptors] = useState([]);
    const [datasetHasDuplicateVariables, setDatasetHasDuplicateVariables] =
        useState(false);
    const [datasetLabel, setDatasetLabel] = useState('');
    const [codeFrameLabels, setCodeFrameLabels] = useState<string[]>([]);
    const [selectedExistingCodeFrameLabel, setSelectedExistingCodeFrameLabel] =
        useState<string>();
    const [useAiGeneratedCodeFrame, setUseAiGeneratedCodeFrame] =
        useState(true);
    const [selectedSheetName, setSelectedSheetName] = useState('');
    const [validationErrorMessage, setValidationErrorMessage] = useState('');

    const initUploadDataset = () => {
        setSelectedVerbatimDescriptors([]);
        setSelectedLangDescriptor(undefined);
        setSelectedIdDescriptor(undefined);
        setSelectedSheetName('');
        setValidationErrorMessage('');
    };

    const initConfigureDataset = () => {
        if (datasetFileDescriptors[0]?.sheetName?.length)
            setSelectedSheetName(datasetFileDescriptors[0].sheetName);
    };
    const initGenerateCodeframe = () => {};

    const QUICKTAG_CREATION_WIZARD_STEPS = [
        {
            id: QuickTagCreationStep.UPLOAD_DATASET,
            component: QuickTagDatasetPage,
            initHandler: initUploadDataset
        },
        {
            id: QuickTagCreationStep.CONFIGURE_DATASET,
            component: QuickTagConfigPage,
            initHandler: initConfigureDataset
        },
        {
            id: QuickTagCreationStep.GENERATE_CODEFRAME,
            component: QuickTagCodeframePage,
            initHandler: initGenerateCodeframe
        }
    ];
    const activeStep = QUICKTAG_CREATION_WIZARD_STEPS[activeStepIdx];

    const ActiveStepComponent = activeStep.component;

    /** Make request to create a QuickTag instance with the data provided from the creation wizard. */
    const createQuickTagInstance = async () => {
        setIsCreating(true);
        const createPromises = selectedVerbatimDescriptors.map(
            async (verbatimFileDescriptor) => {
                const questionLabel =
                    selectedVerbatimDescriptors.length > 1
                        ? `${datasetLabel} (${verbatimFileDescriptor.header})`
                        : datasetLabel;
                const quickTagDetails: FileBasedQuickTagDetails = {
                    instanceType: QUICKTAG_TYPES.FILE,
                    fileType: datasetFileType,
                    fileUrl: datasetFileUrl,
                    verbatimFileDescriptor,
                    idFileDescriptor: selectedIdDescriptor,
                    langFileDescriptor: selectedLangDescriptor,
                    questionLabel
                };
                const data = {
                    quickTagDetails,
                    codeFrameLabel: useAiGeneratedCodeFrame
                        ? null
                        : selectedExistingCodeFrameLabel
                };
                const url = config.api.endpoints.createQuickTagInstanceFromFile;
                return await http.post(url, data);
            }
        );
        try {
            await Promise.all(createPromises);
            setIsCreating(false);
            const alertMsg =
                createPromises.length > 1
                    ? 'Your QuickTag instances have been created and are being analyzed. You will receive an email when each analysis is complete!'
                    : 'You will be emailed when analysis is complete!';
            alert(alertMsg);
            location.reload();
            props.setShowQuickTagCreationWizard(false);
        } catch (err) {
            const alertMsg =
                createPromises.length > 1
                    ? 'Uh-oh! There was an error while trying to create one or more of your QuickTag instances. Please try again later or contact support for assistance.'
                    : 'Uh-oh! There was an error while trying to create a QuickTag instance. Please try again later or contact support for assistance.';
            alert(alertMsg);
            setIsCreating(false);
            console.error(err);
        }
    };

    const getQuickTagCodeFrameLabels = async () => {
        try {
            const url = config.api.endpoints.listQuickTagCodeFrames;
            const response = await http.get(url);
            if (!response.data.labels.length)
                throw new Error(
                    `CodeFrame labels not provided in response with status: ${response.status}`
                );
            else setCodeFrameLabels(response.data.labels);
        } catch (err) {
            console.error(`Could not get code frame labels: ${err}`);
        }
    };

    /** Load CodeFrame labels on mount. */
    useEffect(() => {
        getQuickTagCodeFrameLabels();
    }, []);

    /** Initialize page with initHandler when activeStepIdx changes. */
    useEffect(() => {
        const activeStepInitHandler =
            QUICKTAG_CREATION_WIZARD_STEPS[activeStepIdx].initHandler;
        if (activeStepInitHandler) activeStepInitHandler();
    }, [activeStepIdx]);

    const isAvailableBackButton = () => {
        return activeStepIdx > 0 && !isCreating;
    };

    const isAvailableNextButton = () => {
        if (validationErrorMessage.length) return false;
        if (isCreating) return false;
        else if (activeStep.id == QuickTagCreationStep.UPLOAD_DATASET)
            return Boolean(datasetFileUrl);
        else if (activeStep.id == QuickTagCreationStep.CONFIGURE_DATASET)
            return Boolean(selectedVerbatimDescriptors.length);
        else if (activeStep.id == QuickTagCreationStep.GENERATE_CODEFRAME) {
            return useAiGeneratedCodeFrame || selectedExistingCodeFrameLabel;
        }
    };

    const getNextButtonText = () => {
        return activeStepIdx == QUICKTAG_CREATION_WIZARD_STEPS.length - 1
            ? 'Code'
            : 'Next';
    };

    const onClickBackButton = (e) => {
        const newActiveStepIdx = activeStepIdx - 1;
        setActiveStepIdx(newActiveStepIdx);
    };

    const onClickNextButton = async (e) => {
        if (activeStepIdx < QUICKTAG_CREATION_WIZARD_STEPS.length - 1)
            setActiveStepIdx(activeStepIdx + 1);
        else await createQuickTagInstance();
    };

    const pageProps: QuickTagCreationWizardPageProps = {
        datasetFileType,
        setDatasetFileType,
        datasetFileUrl,
        setDatasetFileUrl,
        datasetFileDescriptors,
        setDatasetFileDescriptors,
        datasetHasDuplicateVariables,
        setDatasetHasDuplicateVariables,
        selectedVerbatimDescriptors,
        setSelectedVerbatimDescriptors,
        selectedIdDescriptor,
        setSelectedIdDescriptor,
        selectedLangDescriptor,
        setSelectedLangDescriptor,
        datasetLabel,
        setDatasetLabel,
        codeFrameLabels,
        selectedExistingCodeFrameLabel,
        setSelectedExistingCodeFrameLabel,
        setUseAiGeneratedCodeFrame,
        useAiGeneratedCodeFrame,
        setSelectedSheetName,
        selectedSheetName
    };
    const isDisabledBackButton = !isAvailableBackButton();
    const isDisabledNextButton = !isAvailableNextButton();
    const nextButtonText = getNextButtonText();

    /** Validate selected data. */
    React.useEffect(() => {
        const isSelectedMoreThanMaxRowsPerInstance =
            selectedVerbatimDescriptors.some(
                (verbatimDescriptor) =>
                    verbatimDescriptor.numRows > QUICKTAG_MAX_ROWS
            );
        const creditsUsedIfAnalyze =
            Number(props.quickTagCredits.creditsUsed) +
            sum(selectedVerbatimDescriptors.map((d) => d.numRows));
        const isNotAvailableCredits =
            Number(props.quickTagCredits.creditsUsed) >
            Number(props.quickTagCredits.creditsTotal);
        const isSelectedMoreThanAvailableCredits =
            creditsUsedIfAnalyze > Number(props.quickTagCredits.creditsTotal);
        if (isSelectedMoreThanMaxRowsPerInstance)
            setValidationErrorMessage(
                `You have selected data which contains more than the ${QUICKTAG_MAX_ROWS} row limit. Please adjust the data selected, or contact support to learn more about analyzing more than ${QUICKTAG_MAX_ROWS} rows of data.`
            );
        else if (isNotAvailableCredits)
            setValidationErrorMessage(
                `You do not have any AI Coding credits available. Please contact inca@nexxt.in to purchase more credits or for support.`
            );
        else if (isSelectedMoreThanAvailableCredits)
            setValidationErrorMessage(
                `You do not have sufficient AI Coding credits available to analyze the data you have selected. Please contact inca@nexxt.in to purchase more credits or for support.`
            );
        else setValidationErrorMessage('');
    }, [selectedVerbatimDescriptors]);

    const ModalTitles = ['Upload Dataset', 'Configure Dataset', 'Code Dataset'];

    return (
        <SplitPaneModal
            icon={<UilChannel size="38px" />}
            header="inca AI Coding"
            isBlurred={true}
            body="Deep human insight at AI scale"
            isOpen={props.showQuickTagCreationWizard}
            onClose={() => props.setShowQuickTagCreationWizard(false)}
        >
            <SplitPaneModalContent header={ModalTitles[activeStepIdx]}>
                <Stepper activeStep={activeStepIdx}>
                    {QUICKTAG_CREATION_WIZARD_STEPS.map((step) => {
                        return (
                            <Step key={step.id}>
                                <StepLabel>{step.id}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                <hr />
                {isCreating ? (
                    <Loader />
                ) : (
                    <ActiveStepComponent {...pageProps} />
                )}
                {Boolean(validationErrorMessage.length) && (
                    <p>{validationErrorMessage}</p>
                )}
            </SplitPaneModalContent>
            <SplitPaneModalActions>
                {activeStepIdx !== 0 && (
                    <button
                        onClick={onClickBackButton}
                        disabled={isDisabledBackButton}
                        className="mr-4"
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={onClickNextButton}
                    disabled={isDisabledNextButton}
                    className="inca-button"
                >
                    {nextButtonText}
                </button>
            </SplitPaneModalActions>
        </SplitPaneModal>
    );
}

export default QuickTagCreationWizard;
